/**
 * Crashes dashboard — lists grouped crash reports from the self-hosted
 * /v1/crash endpoint. Click a row to inspect recent samples for that
 * stack_hash (error_name + first 5 frames normalized).
 */

import { useCallback, useEffect, useState } from "react";
import {
  type CrashGroup,
  type CrashPlatform,
  type CrashSample,
  fetchCrashGroups,
  fetchCrashSamples,
} from "../../api/admin-crashes";
import { ApiError } from "../../api/client";
import { PageHeader } from "../../components/PageHeader";
import { getLocale, t } from "../../i18n";
import { resolveAdminErrorMessage } from "../../lib/admin-error-messages";
import * as shared from "../../styles/shared";

function describeError(err: unknown): string {
  if (err instanceof ApiError) {
    return resolveAdminErrorMessage(err.errorCode, getLocale());
  }
  return "Failed to load";
}

type TimeWindow = 24 | 168 | 720;

type FilterState = {
  readonly platform: CrashPlatform | "";
  readonly sinceHours: TimeWindow;
};

type ViewState = { kind: "list" } | { kind: "detail"; group: CrashGroup };

const INITIAL_FILTER: FilterState = { platform: "", sinceHours: 168 };

export function CrashesPage(): React.JSX.Element {
  const [filter, setFilter] = useState<FilterState>(INITIAL_FILTER);
  const [view, setView] = useState<ViewState>({ kind: "list" });

  return (
    <div>
      <PageHeader
        title={t("crashes.title")}
        description={t("crashes.description")}
      />
      {view.kind === "list" ? (
        <CrashList
          filter={filter}
          onFilter={setFilter}
          onSelect={(group) => setView({ kind: "detail", group })}
        />
      ) : (
        <CrashDetail
          group={view.group}
          onBack={() => setView({ kind: "list" })}
        />
      )}
    </div>
  );
}

// ─── List view ───

function CrashList({
  filter,
  onFilter,
  onSelect,
}: {
  readonly filter: FilterState;
  readonly onFilter: (filter: FilterState) => void;
  readonly onSelect: (group: CrashGroup) => void;
}): React.JSX.Element {
  const [groups, setGroups] = useState<ReadonlyArray<CrashGroup>>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchCrashGroups({
        platform: filter.platform || undefined,
        sinceHours: filter.sinceHours,
      });
      setGroups(data);
    } catch (err) {
      setError(describeError(err));
    } finally {
      setLoading(false);
    }
  }, [filter.platform, filter.sinceHours]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <>
      <section style={shared.section}>
        <div style={filterRow}>
          <label style={filterLabel}>
            {t("crashes.filter_platform")}
            <select
              value={filter.platform}
              onChange={(e) =>
                onFilter({
                  ...filter,
                  platform: (e.target.value as CrashPlatform) || "",
                })
              }
              style={select}
            >
              <option value="">{t("crashes.filter_all")}</option>
              <option value="ios">iOS</option>
              <option value="ipados">iPadOS</option>
              <option value="android">Android</option>
              <option value="macos">macOS</option>
              <option value="windows">Windows</option>
              <option value="admin">Admin</option>
            </select>
          </label>
          <label style={filterLabel}>
            {t("crashes.filter_since")}
            <select
              value={filter.sinceHours}
              onChange={(e) =>
                onFilter({
                  ...filter,
                  sinceHours: Number(e.target.value) as TimeWindow,
                })
              }
              style={select}
            >
              <option value={24}>{t("crashes.since_24h")}</option>
              <option value={168}>{t("crashes.since_7d")}</option>
              <option value={720}>{t("crashes.since_30d")}</option>
            </select>
          </label>
          <button type="button" onClick={load} style={shared.refreshBtn}>
            Refresh
          </button>
        </div>
      </section>
      {error && <p style={shared.errorBanner}>{error}</p>}
      {loading && <p style={shared.loadingText}>{t("common.loading")}</p>}
      {!loading && groups.length === 0 && (
        <p style={shared.loadingText}>{t("crashes.no_groups")}</p>
      )}
      {!loading && groups.length > 0 && (
        <CrashTable groups={groups} onSelect={onSelect} />
      )}
    </>
  );
}

function CrashTable({
  groups,
  onSelect,
}: {
  readonly groups: ReadonlyArray<CrashGroup>;
  readonly onSelect: (group: CrashGroup) => void;
}): React.JSX.Element {
  return (
    <div style={shared.section}>
      <table style={table}>
        <thead>
          <tr>
            <th style={th}>{t("crashes.col_error")}</th>
            <th style={thNarrow}>{t("crashes.col_platform")}</th>
            <th style={thNarrow}>{t("crashes.col_count")}</th>
            <th style={thNarrow}>{t("crashes.col_last_seen")}</th>
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => (
            <tr
              key={g.stack_hash}
              style={row}
              onClick={() => onSelect(g)}
              onKeyDown={(e) => e.key === "Enter" && onSelect(g)}
            >
              <td style={td}>
                <div style={errName}>{g.error_name}</div>
                <div style={errMsg}>{g.error_message}</div>
              </td>
              <td style={tdNarrow}>{g.platform}</td>
              <td style={tdNarrow}>{g.count}</td>
              <td style={tdNarrow}>{formatTime(g.last_seen)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Detail view ───

function CrashDetail({
  group,
  onBack,
}: {
  readonly group: CrashGroup;
  readonly onBack: () => void;
}): React.JSX.Element {
  const [samples, setSamples] = useState<ReadonlyArray<CrashSample>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchCrashSamples(group.stack_hash);
        if (!cancelled) setSamples(data);
      } catch (err) {
        if (!cancelled) setError(describeError(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [group.stack_hash]);

  return (
    <section style={shared.section}>
      <button type="button" onClick={onBack} style={shared.refreshBtn}>
        ← {t("crashes.back_to_list")}
      </button>
      <div style={detailHeader}>
        <h2 style={detailTitle}>{group.error_name}</h2>
        <p style={detailMeta}>
          {group.error_message}
          <br />
          {group.platform} · {t("crashes.group_count", { count: group.count })}
        </p>
      </div>
      {loading && <p style={shared.loadingText}>{t("common.loading")}</p>}
      {error && <p style={shared.errorBanner}>{error}</p>}
      {samples.map((s) => (
        <SampleCard key={s.id} sample={s} />
      ))}
    </section>
  );
}

function SampleCard({
  sample,
}: { readonly sample: CrashSample }): React.JSX.Element {
  return (
    <div style={sampleCard}>
      <div style={sampleHeader}>
        <span style={shared.cardLabel}>
          {formatTime(sample.client_timestamp)}
        </span>
        {sample.is_fatal && (
          <span style={fatalBadge}>{t("crashes.sample_is_fatal")}</span>
        )}
        {sample.app_version && (
          <span style={sampleMeta}>
            {t("crashes.sample_app_version")}: {sample.app_version}
          </span>
        )}
        {sample.os_version && (
          <span style={sampleMeta}>
            {t("crashes.sample_os_version")}: {sample.os_version}
          </span>
        )}
        {sample.device_id_hash && (
          <span style={sampleMeta}>
            {t("crashes.sample_device_hash")}: {sample.device_id_hash}
          </span>
        )}
      </div>
      <pre style={stackBlock}>{sample.stack}</pre>
      {sample.component_stack && (
        <>
          <div style={sectionLabel}>{t("crashes.sample_component_stack")}</div>
          <pre style={stackBlock}>{sample.component_stack}</pre>
        </>
      )}
      {sample.extra !== null && sample.extra !== undefined && (
        <>
          <div style={sectionLabel}>{t("crashes.sample_extra")}</div>
          <pre style={jsonBlock}>{JSON.stringify(sample.extra, null, 2)}</pre>
        </>
      )}
      {sample.breadcrumbs !== null && sample.breadcrumbs !== undefined && (
        <>
          <div style={sectionLabel}>{t("crashes.sample_breadcrumbs")}</div>
          <pre style={jsonBlock}>
            {JSON.stringify(sample.breadcrumbs, null, 2)}
          </pre>
        </>
      )}
    </div>
  );
}

// ─── helpers ───

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

// ─── local styles ───

const filterRow: React.CSSProperties = {
  display: "flex",
  gap: 12,
  alignItems: "flex-end",
  marginBottom: 16,
};

const filterLabel: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 4,
  fontSize: 12,
  color: "var(--color-text-secondary)",
};

const select: React.CSSProperties = {
  padding: "6px 10px",
  fontSize: 13,
  borderRadius: 6,
  border: "1px solid var(--color-border)",
  background: "var(--color-bg)",
  color: "var(--color-text)",
};

const table: React.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: 13,
};

const th: React.CSSProperties = {
  textAlign: "left",
  padding: "8px 12px",
  borderBottom: "1px solid var(--color-border)",
  fontSize: 12,
  color: "var(--color-text-secondary)",
  fontWeight: 600,
};

const thNarrow: React.CSSProperties = { ...th, width: 140 };

const row: React.CSSProperties = {
  cursor: "pointer",
  borderBottom: "1px solid var(--color-border)",
};

const td: React.CSSProperties = { padding: "10px 12px", verticalAlign: "top" };
const tdNarrow: React.CSSProperties = {
  ...td,
  width: 140,
  fontFamily: "var(--font-mono)",
};

const errName: React.CSSProperties = { fontWeight: 600, marginBottom: 4 };
const errMsg: React.CSSProperties = {
  fontSize: 12,
  color: "var(--color-text-secondary)",
  fontFamily: "var(--font-mono)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  maxWidth: 640,
  whiteSpace: "nowrap",
};

const detailHeader: React.CSSProperties = { marginTop: 16, marginBottom: 20 };
const detailTitle: React.CSSProperties = {
  fontSize: 18,
  fontWeight: 600,
  marginBottom: 8,
};
const detailMeta: React.CSSProperties = {
  fontSize: 13,
  color: "var(--color-text-secondary)",
  fontFamily: "var(--font-mono)",
};

const sampleCard: React.CSSProperties = {
  ...shared.card,
  padding: 16,
  marginBottom: 12,
};

const sampleHeader: React.CSSProperties = {
  display: "flex",
  gap: 16,
  flexWrap: "wrap",
  marginBottom: 8,
  fontSize: 12,
};

const sampleMeta: React.CSSProperties = {
  color: "var(--color-text-secondary)",
  fontFamily: "var(--font-mono)",
};

const sectionLabel: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  color: "var(--color-text-secondary)",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  marginTop: 12,
  marginBottom: 4,
};

const stackBlock: React.CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 11,
  lineHeight: 1.5,
  background: "var(--color-code-bg, rgba(0,0,0,0.03))",
  padding: 12,
  borderRadius: 6,
  overflow: "auto",
  maxHeight: 280,
  margin: 0,
};

const jsonBlock: React.CSSProperties = {
  ...stackBlock,
  maxHeight: 180,
};

const fatalBadge: React.CSSProperties = {
  color: "#b91c1c",
  background: "rgba(185, 28, 28, 0.1)",
  padding: "2px 6px",
  borderRadius: 4,
  fontWeight: 600,
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
};
